import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";
import { apiWorkPatterns } from "@/api/work-pattern.api.ts";
import { ApiWorkPatternPayload } from "@/types/work-pattern.types.ts";
import { useMemo, useState } from "react";
import { SelectItem } from "@skailer-com/uikit";
import { escapeRegExp } from "lodash";

const workPatternsKeys = createQueryKeys("workPatterns", {
  all: null,
});

export const useWorkPatterns = (enabled: boolean = true) => {
  return useQuery({
    ...workPatternsKeys.all,
    staleTime: 60 * 1000,
    enabled: enabled,
    queryFn: async () => {
      const result = await apiWorkPatterns.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useWorkPatternsSearchInline = (enabled: boolean = true) => {
  const [searchKey, setSearchKey] = useState<string | undefined>(undefined);

  const { data, isLoading } = useWorkPatterns(enabled);

  const options = useMemo(() => {
    const allData = (data || []) as SelectItem[];
    const regex = new RegExp(escapeRegExp(searchKey), "i");

    return allData.filter((option) => regex.test(option.title));
  }, [data, searchKey]);

  function search(text: string | undefined) {
    setSearchKey(text);
  }

  return {
    data,
    isLoading,
    options,
    search,
  };
};

export const useWorkPatternSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: { id?: number; payload: ApiWorkPatternPayload }) => {
      const result = id
        ? await apiWorkPatterns.update(id, payload)
        : await apiWorkPatterns.create(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workPatternsKeys.all.queryKey });
    },

    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkPatternDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiWorkPatterns.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workPatternsKeys.all.queryKey });
    },

    onError: (error: Error) => {
      onError(error);
    },
  });
};
